"use client";

import { useState, useEffect } from "react";
import { Box, SwipeableDrawer, IconButton, DrawerProps } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { StyleHooks, ProviderHooks } from "@/src/hook";
import { ActionBarDrawer as ActionBarDrawerType } from "@/src/type/strapi/component/partial/ActionBarDrawer";
import { StrapiType, StrapiCollectionType } from "@/src/type";

import ActionBarDrawerActions from "./ActionBarDrawerActions";
import ChatContent from "./ChatContent/ChatContent";
import styles from "./ActionBarDrawer.module.scss";
import { Scheduler } from "@/src/component/view/DynamicPage/component/PropertyUnitDetails/component";

export interface ActionBarDrawerHelpers {
  saveProgress: (progressData: any) => void;
  getProgress: () => any;
  closeDrawer: () => void;
  openDrawer: () => void;
}

interface ActionBarDrawerProps {
  data: ActionBarDrawerType;
  trackingId?: string;
  onOpen?: () => void;
  onClose?: () => void;
  onDataChange?: (data: any) => void;
  restoreSession?: boolean;
  calendarConfig?: StrapiType.StrapiSingleWrapper<StrapiCollectionType.CalendarConfig>;
}

type DrawerContentType = "chat" | "scheduleTour";

function ActionBarDrawer({ data, onOpen, onClose, restoreSession = true, calendarConfig }: ActionBarDrawerProps) {
  const isMobile = StyleHooks.useBreakPointDown("md");
  const [isOpen, setIsOpen] = useState(false);
  const [iOS, setIOS] = useState(false);
  const [drawerContentType, setDrawerContentType] = useState<DrawerContentType | null>(null);
  const [isChatCompact, setIsChatCompact] = useState(true);
  const { getDrawerData, updateDrawerData } = ProviderHooks.useActionBarDrawer();

  const drawerId = `drawer-${data.id}`;

  const getDrawerDirection = (): DrawerProps["anchor"] => {
    if (isMobile) {
      return "bottom";
    }
    return "right";
  };

  const getDrawerDimensions = () => {
    const dimensions: React.CSSProperties = {};

    if (isMobile) {
      dimensions.width = "100%";
      dimensions.maxWidth = "100%";

      if (drawerContentType === "chat" && isChatCompact) {
        dimensions.height = "auto";
        dimensions.maxHeight = "40vh";
      } else if (data.height) {
        dimensions.height = `${data.height}${data.heightUnit || "px"}`;
      } else {
        dimensions.height = "auto";
        dimensions.maxHeight = "80vh";
      }
    } else {
      dimensions.height = "100%";
      dimensions.minWidth = "500px";

      if (data.width) {
        dimensions.width = `${data.width}${data.widthUnit || "px"}`;
      } else {
        dimensions.width = "500px";
      }
    }

    return dimensions;
  };

  const handleToggleDrawer = (open: boolean, contentType?: DrawerContentType) => () => {
    setIsOpen(open);
    if (open && contentType) {
      setDrawerContentType(contentType);
      // Reset chat compact state when switching content types
      if (contentType === "chat") {
        setIsChatCompact(true);
      }
    }

    updateDrawerData(drawerId, {});

    if (open) {
      onOpen?.();
    } else {
      onClose?.();
    }
  };

  const handleOpenChat = handleToggleDrawer(true, "chat");
  const handleOpenScheduleTour = handleToggleDrawer(true, "scheduleTour");
  const handleCloseDrawer = handleToggleDrawer(false);

  const handleChatInputFocus = () => {
    if (isMobile && drawerContentType === "chat") {
      setIsChatCompact(false);
    }
  };

  /*   
  const saveDrawerProgress = (progressData: any) => {
    updateDrawerData(drawerId, {
      progress: {
        ...getDrawerData(drawerId).progress,
        ...progressData,
      },
    });

    if (onDataChange) {
      onDataChange(progressData);
    }
  }; 
  */

  useEffect(() => {
    if (restoreSession) {
      // const savedData = getDrawerData(drawerId);
    }
  }, [drawerId, restoreSession, getDrawerData]);

  useEffect(() => {
    const checkIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIOS(checkIOS);
  }, []);

  return (
    <>
      <ActionBarDrawerActions
        actions={data.actions}
        position={data.position}
        openChat={handleOpenChat}
        openScheduleTour={handleOpenScheduleTour}
        className={styles.ctaButton}
      />

      <SwipeableDrawer
        anchor={getDrawerDirection()}
        open={isOpen}
        onClose={handleCloseDrawer}
        onOpen={handleToggleDrawer(true)}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        disableSwipeToOpen={false}
        sx={{
          zIndex: 1300,
          "& .MuiDrawer-paper": {
            ...getDrawerDimensions(),
            backgroundColor: "#F3EDDF", // moonlight-500
            borderColor: "#3D3D3D", // midnight-500
            borderWidth: "2px",
            borderStyle: "solid",
            borderRadius: isMobile ? "30px 30px 0 0" : "10px 0 0 10px",
          },
        }}
        transitionDuration={{
          appear: 300,
          enter: 300,
          exit: 200,
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Box className={`${styles.drawerContent} ${isMobile && drawerContentType === "chat" && isChatCompact ? styles.chatCompact : ""}`}>
          <Box className={styles.drawerHeader}>
            {drawerContentType === "scheduleTour" && <h2 className={`${styles.drawerTitle} ${styles.centerAligned}`}>schedule tour</h2>}
            {drawerContentType === "chat" && <h2 className={`${styles.drawerTitle} ${styles.leftAligned}`}>ask jess</h2>}
            <IconButton
              onClick={handleCloseDrawer}
              className={styles.closeButton}
              aria-label="Close drawer"
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "#3D3D3D",
                "&:hover": {
                  backgroundColor: "rgba(61, 61, 61, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box className={styles.drawerBody}>
            {drawerContentType === "scheduleTour" && Boolean(data.submitScheduledTourCta) && (
              <Scheduler
                title=""
                scheduleTourCta={data.submitScheduledTourCta}
                calendarConfig={calendarConfig}
                showCtaDivider={true}
                classNames={{
                  container: styles.scheduleTourContainer,
                  header: styles.scheduleTourHeader,
                  content: styles.scheduleTourContent,
                }}
              />
            )}
            {drawerContentType === "chat" && (
              <ChatContent
                onInputFocus={handleChatInputFocus}
                onInputBlur={() => {}}
                isChatCompact={isChatCompact}
              />
            )}
          </Box>
        </Box>
      </SwipeableDrawer>
    </>
  );
}

export default ActionBarDrawer;
